import React, { memo, useMemo, useState } from 'react';
import { Image, Upload } from 'antd';
import { collect } from 'collect.js';
import { PlusOutlined } from '@ant-design/icons';
import ImageCropper from 'antd-img-crop';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const FileAction = {
  add: 'add',
  remove: 'remove',
};

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const compressImage = (file, maxWidth, quality) =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      let { width, height } = img;

      if (width <= maxWidth) {
        URL.revokeObjectURL(img.src);
        resolve(file);

        return;
      }

      height = Math.round((height * maxWidth) / width);
      width = maxWidth;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(img.src);

      canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: file.type })), file.type, quality);
    };

    img.src = URL.createObjectURL(file);
  });

function DraggableUploadListItem({ originNode, file, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.uid,
    disabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'move',
    height: '100%',
    width: '100%',
  };

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'is-dragging' : ''} {...attributes} {...listeners}>
      {file.status === 'error' && isDragging ? originNode.props.children : originNode}
    </div>
  );
}

export default function ImagePicker({
  images,
  placeholder,
  onChange,
  onAdd,
  onDelete,
  onReorder,
  beforeUpload,
  withDnd,
  withCropper,
  cropperProps = {},
  maxCount = 1,
  format = 'image/png',
  maxOutputWidth = 1920,
  outputQuality = 0.85,
  loadingImageId = null,
}) {
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });

  const files = useMemo(
    () =>
      collect(images)
        .filter((item) => Boolean(item))
        .map((item, index) => ({
          uid: `image-${index}`,
          status: 'done',
          file: item,
          url: item instanceof File ? URL.createObjectURL(item) : item,
        }))
        .toArray(),
    [images, loadingImageId],
  );

  const commitChanges = (callback) => {
    setLoading(true);
    callback().finally(() => setLoading(false));
  };

  const handleChange = (file, action) => {
    if (action === FileAction.remove) {
      onChange(collect(files).where('uid', '!=', file.uid).pluck('file').toArray());

      return commitChanges(async () => onDelete && onDelete(file));
    }

    onChange(collect(files).pluck('file').push(file).toArray());

    return commitChanges(async () => onAdd && onAdd(file));
  };

  const onDragEnd = ({ active, over }) => {
    if (active.id === over?.id) {
      return;
    }

    const activeIndex = files.findIndex((i) => i.uid === active.id);
    const overIndex = files.findIndex((i) => i.uid === over?.id);
    const result = collect(arrayMove(files, activeIndex, overIndex))
      .pluck('file')
      .toArray();

    onChange(result);

    return commitChanges(async () => onReorder && onReorder(result));
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const Wrapper = memo(({ withDnd, withCropper, children }) =>
    withDnd ? (
      <DndContext sensors={[sensor]} onDragEnd={onDragEnd}>
        <SortableContext items={files.map((i) => i.uid)} strategy={horizontalListSortingStrategy}>
          {withCropper ? (
            <ImageCropper rotationSlider aspectSlider fillColor="transparent" {...cropperProps}>
              {children}
            </ImageCropper>
          ) : (
            children
          )}
        </SortableContext>
      </DndContext>
    ) : withCropper ? (
      <ImageCropper rotationSlider aspectSlider fillColor="transparent" {...cropperProps}>
        {children}
      </ImageCropper>
    ) : (
      children
    ),
  );

  const onUpload = (file) => {
    const process = async (raw) => {
      const processed = withCropper ? await compressImage(raw, maxOutputWidth, outputQuality) : raw;

      if (beforeUpload) {
        await beforeUpload(processed);
      }

      handleChange(processed, FileAction.add);
    };

    process(file);

    return false;
  };

  return (
    <Wrapper withDnd={withDnd} withCropper={withCropper}>
      <Upload
        listType="picture-card"
        fileList={files}
        onPreview={handlePreview}
        onRemove={(file) => handleChange(file, FileAction.remove)}
        beforeUpload={onUpload}
        maxCount={maxCount}
        accept={format}
        itemRender={(originNode, file) => (
          <DraggableUploadListItem originNode={originNode} file={file} disabled={loading} />
        )}
      >
        {files.length >= maxCount ? null : (
          <button style={{ border: 0, background: 'none' }} type="button">
            {placeholder ? (
              <img src={placeholder} alt="" className="max-w-[100%]" />
            ) : (
              <>
                <PlusOutlined />
                <div className="mt-xs">Upload</div>
              </>
            )}
          </button>
        )}
      </Upload>
      {previewImage && (
        <Image
          alt=""
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}
    </Wrapper>
  );
}
